import React, { createContext, useCallback, useContext, useMemo, useRef, useState, useEffect } from 'react';

const AudioPlayerContext = createContext(null);

export function AudioPlayerProvider({ children }) {
  const audioRef = useRef(null);
  const currentTrackRef = useRef(null);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState({ currentTime: 0, duration: 0 });
  const [volume, setVolume] = useState(1);

  useEffect(() => {
    currentTrackRef.current = currentTrack;
  }, [currentTrack]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);
    const handleTimeUpdate = () => {
      setProgress({
        currentTime: audio.currentTime || 0,
        duration: audio.duration || 0,
      });
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleTimeUpdate);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleTimeUpdate);
    };
  }, []);

  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.volume = volume;
  }, [volume]);

  const playTrack = useCallback(async (track, options = {}) => {
    if (!track?.url) throw new Error('Track URL is required to play audio');
    const audio = audioRef.current;
    if (!audio) return;

    const isSameTrack = currentTrackRef.current?.id && track.id && currentTrackRef.current.id === track.id;
    const shouldResetSource = !isSameTrack || audio.src !== track.url;

    if (shouldResetSource) {
      audio.src = track.url;
      audio.currentTime = options.startTime ?? 0;
      setCurrentTrack({
        id: track.id ?? track.songId ?? track.url,
        title: track.title || track.name || 'Untitled',
        artistName: track.artist || track.artistName || 'Unknown Artist',
        artwork: track.artwork || track.coverUrl || track.imageUrl || null,
        url: track.url,
      });
    } else if (typeof options.startTime === 'number') {
      audio.currentTime = options.startTime;
    }

    try {
      await audio.play();
    } catch (err) {
      // Silently handle autoplay policy errors (expected browser behavior)
      // Only log unexpected errors
      if (err.name !== 'NotAllowedError') {
        console.error('Failed to start playback', err);
      }
      // Don't throw for autoplay policy errors - they're expected
      if (err.name !== 'NotAllowedError') {
        throw err;
      }
    }
  }, []);

  const resume = useCallback(async () => {
    if (!currentTrack) return;
    const audio = audioRef.current;
    if (!audio) return;
    if (!audio.paused) return;
    await audio.play();
  }, [currentTrack]);

  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
  }, []);

  const seek = useCallback((timeInSeconds) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.min(Math.max(timeInSeconds, 0), audio.duration || timeInSeconds);
  }, []);

  const value = useMemo(() => ({
    audioRef,
    currentTrack,
    isPlaying,
    progress,
    volume,
    setVolume,
    playTrack,
    pause,
    resume,
    seek,
  }), [currentTrack, isPlaying, playTrack, pause, progress, resume, seek, volume]);

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
      <audio
        ref={audioRef}
        preload="auto"
        playsInline
        className="global-audio-element"
      />
    </AudioPlayerContext.Provider>
  );
}

export function useAudioPlayer() {
  const ctx = useContext(AudioPlayerContext);
  if (!ctx) {
    throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
  }
  return ctx;
}


